import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  exportInputValuesFailure,
  exportInputValuesModalMinimize,
  exportInputValuesProgressModalClose,
  exportInputValuesSuccess,
  getExportValuesState,
  getJobId,
} from '@app/features/exportInputValues';
import ExportInputValuesProgressModal from '@app/features/exportInputValues/components/ExportInputValuesProgressModal';
import ExportInputValuesProgressModalSnackBar from '@app/features/exportInputValues/components/ExportInputValuesProgressModalSnackBar';
import useExportInputValuesProgressBarPercent from '@app/features/exportInputValues/hooks/useExportInputValuesProgressBarPercent';
import useExportInputValuesStatus from '@app/features/exportInputValues/hooks/useExportInputValuesStatus';
import useIsPrivateWithPublicWorkspace from '@app/features/workspaceManagement/hooks/useIsPrivateWithPublicWorkspace';
import coalesceGlobalErrorMessage from '@app/libs/coalesceGlobalErrorMessage';

export default function ExportInputValuesSnackBarContainer() {
  const dispatch = useDispatch();
  const isPrivateWithPublicWorkspace = useIsPrivateWithPublicWorkspace();
  const exportInputValuesState = useSelector(getExportValuesState);
  const jobId = useSelector(getJobId) ?? '';

  // 서버의 부하를 줄이기 위해 1초에서 5초로 폴링 간격을 변경하였습니다.
  const REFETCH_INTERVAL = 5000;

  const [refetchInterval, setRefetchInterval] = useState<number | false>(REFETCH_INTERVAL);

  const { data, error } = useExportInputValuesStatus(
    { jobId },
    {
      /**
       * 타임아웃 처리는 서버에서 담당하고, 기준은 1000건 미만 10분 1000건 이상 100분입니다.
       * 클라이언트에서는 IN_PROGRESS 상태일 때만 폴링하고, 이후 추출이 완료되면 폴링을 중단합니다.
       */
      refetchInterval,
      enabled:
        exportInputValuesState.status === 'IN_PROGRESS' ||
        exportInputValuesState.status === 'COMPLETED',
      onError: () => {
        setRefetchInterval(false);
      },
      onSuccess: ({
        documentCount,
        failureMessage,
        failureDocumentCount,
        status,
        successDocumentCount,
        url,
      }) => {
        if (status === 'DONE') {
          dispatch(
            exportInputValuesSuccess({
              failureCount: failureDocumentCount,
              successCount: successDocumentCount,
              totalCount: documentCount,
              url,
            })
          );
          setRefetchInterval(false);
        }
        if (status === 'FAILED' || status === 'ABORTED') {
          dispatch(
            exportInputValuesFailure({
              errorMessage:
                coalesceGlobalErrorMessage(failureMessage ?? '', {
                  'Job timeout': '입력값 추출 제한 시간이 만료되었습니다. 다시 시도해 주세요.',
                  'Count limit of columns exceeded':
                    '추출할 수 있는 입력값의 최대 개수를 초과하였습니다.',
                }) ?? '입력값 추출을 실패했습니다. 다시 시도해 주세요.',
              failureCount,
              successCount,
              totalCount,
              url,
            })
          );
        }
      },
    }
  );

  const failureCount = data?.failureDocumentCount ?? 0;
  const successCount = data?.successDocumentCount ?? 0;
  const totalCount = data?.documentCount ?? 0;
  const progressCount = Math.floor((data?.inProgressDocumentCount ?? 0) / 2);
  const currentProgressCount = Math.floor(failureCount + successCount + progressCount);
  const completedCount = failureCount + successCount;

  const { progressPercent, resetEstimatedCSVCreationTime } = useExportInputValuesProgressBarPercent(
    {
      totalCount,
      completedCount,
      currentProgressCount,
      status: data?.status,
    }
  );

  useEffect(() => {
    setRefetchInterval(REFETCH_INTERVAL);
    resetEstimatedCSVCreationTime();
  }, [jobId, resetEstimatedCSVCreationTime]);

  if (isPrivateWithPublicWorkspace) {
    return null;
  }

  if (
    (exportInputValuesState.status !== 'IN_PROGRESS' &&
      exportInputValuesState.status !== 'COMPLETED' &&
      exportInputValuesState.status !== 'FAILED') ||
    data?.status === 'ABORTED'
  ) {
    return null;
  }

  return (
    <>
      <ExportInputValuesProgressModal
        data={data}
        error={error}
        isOpen
        progressPercent={progressPercent}
        onClose={() => {
          dispatch(exportInputValuesProgressModalClose());
          resetEstimatedCSVCreationTime();
        }}
      />
      <ExportInputValuesProgressModalSnackBar
        failedDocumentCount={failureCount}
        progressPercent={progressPercent}
        status={exportInputValuesState.status}
        onMaximize={() => {
          dispatch(exportInputValuesModalMinimize({ isMinimized: false }));
        }}
      />
    </>
  );
}
